import {
  Classes,
  Menu,
  MenuItem,
  Position,
  Pre,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import {
  ItemListRenderer,
  ItemRendererProps,
  MultiSelect,
} from '@blueprintjs/select';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import uniq from 'lodash.uniq';
import * as React from 'react';

import { StringInput } from 'bones/components/atoms/inputs/StringInput';
import { NoResultsMenuItem } from 'bones/components/atoms/NoResultsMenuItem';
import { Tooltip } from 'bones/components/atoms/Tooltip';
import { useQueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import { noop } from 'bones/utils/noop';
import { INSTANCE_SPLIT_SYMBOL } from 'bones/utils/queryBuilder/constants';
import {
  INSTANCE_BACKTICKS_MATCHER,
  INSTANCE_COMMAS_MATCHER,
} from 'bones/utils/queryBuilder/matchers';
import { validateInstanceValue } from 'bones/utils/queryBuilder/validation/validateInstanceValue';
import { createNewSelectItemFromQuery } from 'bones/utils/select/createNewSelectItemFromQuery';
import { SelectItem } from 'bones/utils/select/types';

// eslint-disable-next-line @blueprintjs/no-deprecated-components
const InstanceMultiSelect = MultiSelect.ofType<SelectItem>();

const createNewItemRenderer =
  ({
    createText,
    testId,
    validator,
  }: {
    createText: string;
    testId?: string;
    validator: ReturnType<typeof validateInstanceValue>;
  }) =>
  // eslint-disable-next-line react/display-name
  (
    query: string,
    active: boolean,
    handleClick: React.MouseEventHandler<HTMLElement>
  ) => {
    // eslint-disable-next-line react/destructuring-assignment
    const trimmedQuery = query.trim();
    if (!trimmedQuery || !trimmedQuery.startsWith('`')) {
      return undefined;
    }

    const { isValid } = validator(trimmedQuery);

    return (
      <MenuItem
        active={active}
        data-testid={testId}
        disabled={!isValid}
        icon={IconNames.ADD}
        onClick={handleClick}
        shouldDismissPopover={false}
        text={
          <>
            {createText}
            <span className={Classes.MONOSPACE_TEXT}>{trimmedQuery}</span>
          </>
        }
      />
    );
  };

const itemListRenderer =
  (isLoading: boolean): ItemListRenderer<SelectItem> =>
  // eslint-disable-next-line react/display-name
  ({ items, renderCreateItem, renderItem }) => {
    const allItems = [renderCreateItem(), ...items.map(renderItem)].filter(
      (item): item is JSX.Element => Boolean(item)
    );

    let body: JSX.Element | JSX.Element[];
    if (isLoading) {
      body = <Spinner size={SpinnerSize.SMALL} />;
    } else if (allItems.length) {
      body = allItems;
    } else {
      body = <NoResultsMenuItem />;
    }

    return <Menu>{body}</Menu>;
  };

const itemRenderer =
  (
    itemTestId: (id: string, idx?: number) => string,
    currentItems: SelectItem[]
  ) =>
  // eslint-disable-next-line react/display-name
  (
    { text, value }: SelectItem,
    { handleClick, modifiers, index }: ItemRendererProps
  ) => {
    return (
      <MenuItem
        key={value}
        active={modifiers.active}
        data-testid={itemTestId(value, index)}
        icon={
          currentItems.some((item) => item.value === value)
            ? IconNames.TICK
            : IconNames.BLANK
        }
        onClick={(evt) => {
          evt.stopPropagation();
          handleClick(evt);
        }}
        text={text}
      />
    );
  };

export interface InstanceValueProps {
  invalid?: boolean;
  onSetValue: (value: string) => void;
  subject: string;
  tooltip: string;
  value: string | null;
}

const InstanceSuggestions: React.VFC<InstanceValueProps> = ({
  invalid,
  onSetValue,
  subject,
  tooltip,
  value: rawValue,
}) => {
  const values =
    rawValue &&
    rawValue.match(INSTANCE_BACKTICKS_MATCHER) &&
    rawValue.match(INSTANCE_COMMAS_MATCHER)
      ? rawValue.split(INSTANCE_SPLIT_SYMBOL).filter(Boolean)
      : [];

  const { copy, testIds, useInstanceSuggestions, useInstanceLabels } =
    useQueryBuilderSchemaContext();
  const valueValidator = validateInstanceValue(copy);

  const [queryKey, setQueryKey] = React.useState('');
  const debouncedSetQueryKey = React.useMemo(
    () =>
      process.env.NODE_ENV === 'test'
        ? setQueryKey
        : /* istanbul ignore next */ debounce(setQueryKey, 500),
    []
  );

  const { suggestions, isLoading } = useInstanceSuggestions?.(
    subject,
    queryKey
  ) || /* istanbul ignore next */ {
    suggestions: [],
    isLoading: false,
  };

  const { labelsByIri } = useInstanceLabels?.(
    values.map((value) => value.replace(/`/g, ''))
  ) || {
    labelsByIri: {},
    isLoading: false,
  };

  const currentItems = values.map((value) => {
    const foundItem = suggestions.find((item) => item.value === value);
    if (foundItem) {
      return foundItem;
    }

    const foundLabel = labelsByIri[value.replace(/`/g, '')];
    if (foundLabel) {
      return { text: foundLabel, value };
    }

    return { text: value, value, isNewlyCreated: true };
  });

  const [query, setQuery] = React.useState('');
  const queryTooltip = query.startsWith('`')
    ? valueValidator(query.trim()).tooltip
    : tooltip;

  const handleSetQuery = React.useCallback(
    (nextQuery: string) => {
      if (query === nextQuery) {
        return;
      }

      setQuery(nextQuery);

      const trimmed = nextQuery.trim();
      if (!trimmed || trimmed.startsWith('`')) {
        setQueryKey('');
      } else {
        debouncedSetQueryKey(trimmed);
      }
    },
    [debouncedSetQueryKey, query]
  );

  const handleAddItem = React.useCallback(
    (item: SelectItem) => {
      onSetValue(
        uniq([
          ...currentItems.map(({ value }) => value),
          ...(item.isNewlyCreated
            ? item.value.split(INSTANCE_SPLIT_SYMBOL).filter(Boolean)
            : [item.value]),
        ]).join(INSTANCE_SPLIT_SYMBOL)
      );
    },
    [currentItems, onSetValue]
  );

  const handleRemoveItem = React.useCallback(
    (item: SelectItem) => {
      onSetValue(
        currentItems
          .filter((current) => current.value !== item.value)
          .map(({ value }) => value)
          .join(INSTANCE_SPLIT_SYMBOL)
      );
    },
    [currentItems, onSetValue]
  );

  const handleSelectItem = React.useCallback(
    (item: SelectItem) => {
      if (!valueValidator(item.value).isValid) {
        return;
      }

      if (
        !item.isNewlyCreated &&
        currentItems.some((currentItem) => item.value === currentItem.value)
      ) {
        handleRemoveItem(item);
      } else {
        handleAddItem(item);
        if (item.isNewlyCreated) {
          handleSetQuery('');
        }
      }
    },
    [
      currentItems,
      handleAddItem,
      handleRemoveItem,
      handleSetQuery,
      valueValidator,
    ]
  );

  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);

  return (
    <InstanceValueWrapper
      invalid={invalid}
      isOpen={isTooltipOpen}
      tooltip={queryTooltip}
    >
      <InstanceMultiSelect
        createNewItemFromQuery={(query) =>
          createNewSelectItemFromQuery(query.trim())
        }
        createNewItemPosition="first"
        createNewItemRenderer={createNewItemRenderer({
          createText: copy.body.instance.createNew,
          testId: testIds.instanceSelectorNewItem,
          validator: valueValidator,
        })}
        itemListRenderer={itemListRenderer(isLoading)}
        itemRenderer={itemRenderer(testIds.instanceSelectorItem, currentItems)}
        items={suggestions}
        noResults={<NoResultsMenuItem />}
        onItemSelect={handleSelectItem}
        onItemsPaste={noop}
        onQueryChange={handleSetQuery}
        onRemove={handleRemoveItem}
        popoverProps={{
          minimal: true,
          onClose: () => {
            handleSetQuery('');
          },
          popoverClassName: 'sd-popover-menu',
          portalContainer:
            (process.env.NODE_ENV === 'test' &&
              document.getElementById('vet-bones-root')) ||
            // istanbul ignore next
            undefined,
          targetClassName: 'sd-query-builder-instance-value-target',
          targetTagName: 'div',
          wrapperTagName: 'div',
        }}
        query={query}
        selectedItems={currentItems}
        tagInputProps={{
          inputProps: {
            className: classNames({
              [Classes.MONOSPACE_TEXT]: query.startsWith('`'),
            }),
            'data-testid': testIds.instanceSelectorInput,
            onMouseEnter: () => setIsTooltipOpen(true),
            onMouseLeave: () => setIsTooltipOpen(false),
          } as any,
          placeholder: '',
          tagProps: { minimal: true },
        }}
        tagRenderer={(item) => (
          <span
            className={classNames({
              [Classes.MONOSPACE_TEXT]: item.isNewlyCreated,
            })}
          >
            {item.text}
          </span>
        )}
      />
    </InstanceValueWrapper>
  );
};

const InstanceValueWrapper: React.VFC<
  Pick<InstanceValueProps, 'invalid' | 'tooltip'> & {
    children: React.ReactNode;
    isOpen?: boolean;
  }
> = ({ children, isOpen, invalid, tooltip }) => {
  const { copy, testIds } = useQueryBuilderSchemaContext();
  return (
    <div
      className={classNames('sd-query-builder-instance-value', {
        'sd-query-builder-instance-value--invalid': invalid,
      })}
      data-testid={testIds.instanceSelector}
    >
      <Tooltip
        className="sd-query-builder-instance-value-tooltip-target"
        content={tooltip || copy.body.instance.valueTooltip}
        isOpen={isOpen}
        openOnTargetFocus={false}
        popoverClassName="sd-query-builder-instance-value-tooltip"
        position={Position.LEFT}
        targetProps={
          {
            'data-testid': testIds.instanceSelectorTooltipTarget,
          } as any
        }
        targetTagName="div"
      >
        {children}
      </Tooltip>
    </div>
  );
};

export const InstanceValue: React.VFC<InstanceValueProps> = ({
  invalid,
  onSetValue,
  subject,
  tooltip,
  value,
}) => {
  const { testIds, useInstanceSuggestions } = useQueryBuilderSchemaContext();

  return useInstanceSuggestions ? (
    <InstanceSuggestions
      invalid={invalid}
      onSetValue={onSetValue}
      subject={subject}
      tooltip={tooltip}
      value={value}
    />
  ) : (
    <InstanceValueWrapper invalid={invalid} tooltip={tooltip}>
      <StringInput
        inputClassName={Classes.MONOSPACE_TEXT}
        inputTestId={testIds.instanceSelectorInput}
        onSetValue={(val) => onSetValue(val.trim())}
        value={value}
      />
      <div
        className={classNames(
          'sd-query-builder-instance-value--hidden',
          Classes.MONOSPACE_TEXT
        )}
      >
        <Pre>{value}</Pre>
      </div>
    </InstanceValueWrapper>
  );
};
